import React, { FC } from 'react';

import {
  FeatureGroupModel,
  PackageCardBadgeModel,
  PackageCardModel,
  PackagesCardModel,
  SelectPackageFunction,
  TextBlockModel,
} from '@vp/digital-common-data-model-lib';
import { FeatureGroupNewTablesHeaderContainer } from '@vp/digital-feature-groups-table-lib';

import '@vp/digital-pricing-lib/dist/index.css';
import '@vp/digital-feature-groups-table-lib/dist/index.css';

const WixNewPackageGridHeader: FC<{
  packageGridPlans: PackagesCardModel;
  productVersion: number;
  plansBadges?: {
    [key: string]: PackageCardBadgeModel;
  };
  plansDescriptions?: {
    [key: string]: string;
  };
  highlightedPlans?: Array<string>;
  disabledPlans?: Array<string>;
  plansCtasIndex?: Array<number>;
  selectPackageCallback?: SelectPackageFunction;
  isLoading?: boolean;
  isBilledYearly?: boolean;
  overrideSales?: boolean;
  className?: string;
  buttonsPosition?: 'top' | 'bottom';
  defaultSkin?:
    | 'link'
    | 'primary'
    | 'secondary'
    | 'unstyled'
    | 'tertiary'
    | 'clear-selection';
  accessibility: { translations: FeatureGroupModel; ariaLabelledby?: string };
}> = ({
  packageGridPlans,
  productVersion,
  plansBadges,
  plansDescriptions,
  highlightedPlans,
  disabledPlans,
  plansCtasIndex,
  selectPackageCallback,
  isLoading,
  isBilledYearly,
  overrideSales,
  defaultSkin = 'primary',
  buttonsPosition = 'top',
  className,
  accessibility,
}) => {
  const principalFeatureGroups = [[0], [0, 2]];

  const content: PackagesCardModel = {
    ...packageGridPlans,
    packages: packageGridPlans.packages.map(
      (packageCard: PackageCardModel, index: number): PackageCardModel => ({
        ...packageCard,
        badge:
          plansBadges?.[packageCard.key] ||
          (!disabledPlans?.includes(packageCard.key) && packageCard.badge),
        isHighlighted: highlightedPlans?.includes(packageCard.key),
        isDisabled: disabledPlans?.includes(packageCard.key),
        isLoading: isLoading,
        selectPackageButtons: [
          {
            ...packageCard.selectPackageButtons[
              plansCtasIndex ? plansCtasIndex[index] : 0
            ],
            description:
              plansDescriptions?.[packageCard.key] ||
              (disabledPlans?.includes(packageCard.key) &&
                packageCard.selectPackageButtons[
                  plansCtasIndex ? plansCtasIndex[index] : 0
                ].description),
            onPackageSelected: selectPackageCallback,
            skin: defaultSkin,
          },
        ],
        featureGroups: packageCard.featureGroups.map(
          (
            featureGroup: FeatureGroupModel,
            index: number
          ): FeatureGroupModel => {
            const categoryIndex: number =
              packageGridPlans?.packageCategories?.findIndex(
                (packageCategorie: TextBlockModel) =>
                  packageCategorie.key === packageCard.key
              );
            return {
              ...featureGroup,
              isPrincipal:
                categoryIndex >= 0 &&
                principalFeatureGroups[categoryIndex].includes(index),
            };
          }
        ),
      })
    ),
  };

  const { translations, ariaLabelledby } = accessibility;
  const accessibilityProps = {
    accessibleTextForDots: content.packages
      ? content.packages.map(
          (packageGrid, index) =>
            translations.features[5]?.title.replace(
              '{{SLIDE_NUMBER}}',
              String(index + 1)
            ) ?? ''
        )
      : [],
    accessibleTextForVisibleSlides: translations.features[2]?.valueText,
    accessibleTextForSlide: translations.features[2]?.altText,
    accessibleTextForNext: translations.features[4].title,
    accessibleTextForPrevious: translations.features[3].title,
    ariaRoledescription: translations.features[0].title,
    ariaLabel: translations.features[13].title,
    ariaLabelledby: ariaLabelledby,
    slideAriaRoledescription: translations.features[1].title,
  };
  if (ariaLabelledby) {
    delete accessibilityProps.ariaLabel;
  }

  return (
    <FeatureGroupNewTablesHeaderContainer
      content={content}
      productVersion={productVersion}
      isAnnualBilling={isBilledYearly}
      overrideSales={overrideSales}
      buttonsPosition={buttonsPosition}
      className={className}
      accessibility={accessibilityProps}
    />
  );
};

export default WixNewPackageGridHeader;
